import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  BillingPolicy,
  ExecutionStatus,
  ExecutionSummary,
  ExecutionView,
  IntegritySignalQuery,
  IntegritySignalRow,
  SiteModeRow,
  StartExecutionRequest,
  StartExecutionResponse,
  UsageDailyRow
} from '../models/agents.model';

/**
 * Internal review surface of the Plus Agent Platform (specs/agents/CONTRACT.md §3, the
 * `api/admin/agents/*` rows). Lives on the AI assistant API, not the account API; the bearer
 * token goes on exactly as for every other admin call (AuthInterceptor) and the server's
 * PlatformAdmin policy admits Support and SuperAdmin.
 */
@Injectable({ providedIn: 'root' })
export class AgentsService {
  private readonly baseUrl = environment.aiApiBaseUrl + '/api/admin/agents';

  constructor(private http: HttpClient) {}

  listExecutions(filter: { status?: ExecutionStatus | string | null; businessId?: string | null; take?: number } = {}): Observable<ExecutionSummary[]> {
    let params = new HttpParams();
    if (filter.status) params = params.set('status', filter.status);
    if (filter.businessId) params = params.set('businessId', filter.businessId);
    if (filter.take) params = params.set('take', filter.take);
    return this.http.get<ExecutionSummary[]>(`${this.baseUrl}/executions`, { params });
  }

  getExecution(id: string): Observable<ExecutionView> {
    return this.http.get<ExecutionView>(`${this.baseUrl}/executions/${encodeURIComponent(id)}`);
  }

  /** Starts an execution for a business as the platform (actor kind `platform`). 409 = one is already active. */
  startExecution(agentKey: string, businessId: string, request: StartExecutionRequest): Observable<StartExecutionResponse> {
    return this.http.post<StartExecutionResponse>(
      `${this.baseUrl}/${encodeURIComponent(agentKey)}/executions/${encodeURIComponent(businessId)}`,
      request
    );
  }

  /**
   * "Send to agent": the agent edits the current draft and saves a new version; on a Plus Intelligence case the
   * instruction is a follow-up (CONTRACT-v1.2 §4.3). At most 2,000 characters: 400 `instruction.too-long` over that.
   */
  requestRevision(id: string, instruction: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/executions/${encodeURIComponent(id)}/revisions`, { instruction });
  }

  /** Fresh plan, same execution. */
  regenerate(id: string, note?: string | null): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/executions/${encodeURIComponent(id)}/regenerate`, { note: note ?? null });
  }

  /**
   * "Just note it": status ChangesRequested, no model run. Website builds only: a live Plus Intelligence case
   * refuses it with 422 `case.use-case-actions`, a finished one with 409 (AI_Assistant `19bc1ea`).
   */
  requestChanges(id: string, note: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/executions/${encodeURIComponent(id)}/request-changes`, { note });
  }

  /**
   * Writes the approval record, then the execution publishes (FR-11.3). Website builds only: the runtime
   * has no publish action for `plus-intelligence` (422 "has no publish action"), whose proposal the business
   * approves option by option through the owner facade (CONTRACT-v1.2 §4), so the card never offers this on a case.
   */
  approve(id: string, note?: string | null): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/executions/${encodeURIComponent(id)}/approve`, { note: note ?? null });
  }

  /** Refused with 409 once the execution is Publishing or Published. */
  cancel(id: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/executions/${encodeURIComponent(id)}/cancel`, {});
  }

  /** Aggregate usage; `from`/`to` are ISO dates (yyyy-MM-dd). */
  getUsage(from: string, to: string, businessId?: string | null): Observable<UsageDailyRow[]> {
    let params = new HttpParams().set('from', from).set('to', to);
    if (businessId) params = params.set('businessId', businessId);
    return this.http.get<UsageDailyRow[]>(`${this.baseUrl}/usage`, { params });
  }

  /**
   * The business's model billing policy with the month's spend (CONTRACT-v1.2 §2 `billing-policy`,
   * read through the AI API's admin route); the platform default when nothing is stored for it.
   */
  getBillingPolicy(businessId: string): Observable<BillingPolicy> {
    return this.http.get<BillingPolicy>(`${this.baseUrl}/billing-policy/${encodeURIComponent(businessId)}`);
  }

  /**
   * Integrity signals across businesses, newest first (CONTRACT-v1.3 §9 `GET api/admin/agents/
   * integrity-signals`, passing through the account API list of §2). Only the filters that are
   * set are sent; `take` is 1–500 on the server, which defaults it to 100.
   */
  integritySignals(query: IntegritySignalQuery = {}): Observable<IntegritySignalRow[]> {
    let params = new HttpParams();
    const keys: Array<keyof IntegritySignalQuery> = ['businessId', 'agentKey', 'kind', 'severity', 'from', 'to', 'take'];
    for (const key of keys) {
      const value = query[key];
      if (value === null || value === undefined || value === '') continue;
      params = params.set(key, String(value));
    }
    return this.http.get<IntegritySignalRow[]>(`${this.baseUrl}/integrity-signals`, { params });
  }

  /**
   * Every website of the business with a stored build mode, who set it and when (CONTRACT-v1.5 §7,
   * FR-55.8): `GET api/admin/agents/website-builder/{businessId}/site-modes`, the AI API's admin read of
   * the account API's `GET site-modes/{businessId}` (§7.2). A website with no row builds in hybrid.
   */
  getSiteModes(businessId: string): Observable<SiteModeRow[]> {
    return this.http.get<SiteModeRow[]>(`${this.baseUrl}/website-builder/${encodeURIComponent(businessId)}/site-modes`);
  }
}
